import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Instagram, Linkedin, Facebook, Link as LinkIcon, ArrowRight } from 'lucide-react';
import { FOOTER } from '@/constants/testIds';
import { LOGO_URL, SOCIAL_LINKS } from '@/constants/content';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TikTok icon (Lucide has no built-in TikTok icon)
function TikTokIcon({ size = 18, strokeWidth = 1.4 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

export default function Footer() {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email: email.trim(),
        first_name: firstName.trim(),
        source: 'footer',
        consent_given: true,
        website,
      });
      setDone(true);
      setEmail('');
      setFirstName('');
      toast.success("You're on The Founder's List.");
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error('Too many attempts. Please wait a moment.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid={FOOTER.root} className="bg-matte text-ivory">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <img src={LOGO_URL} alt="Toia Wallace" className="h-14 w-auto object-contain brightness-0 invert opacity-90" />
            <p className="mt-8 font-serif-display text-3xl lg:text-4xl leading-[1.15] text-ivory/90 max-w-md">
              Building companies, technology, and AI-powered systems that help entrepreneurs launch, grow, and scale smarter.
            </p>
          </div>

          <div className="lg:col-span-3">
            <div className="eyebrow text-ivory/60 mb-6">Explore</div>
            <ul className="space-y-3 font-serif-display text-xl">
              <li><Link to="/about" className="hover:text-gold transition-colors">About</Link></li>
              <li><Link to="/companies" className="hover:text-gold transition-colors">Companies</Link></li>
              <li><Link to="/speaking" className="hover:text-gold transition-colors">Speaking</Link></li>
              <li><Link to="/media" className="hover:text-gold transition-colors">Media</Link></li>
              <li><Link to="/credentials" className="hover:text-gold transition-colors">Credentials</Link></li>
              <li><Link to="/journal" className="hover:text-gold transition-colors">Journal</Link></li>
              <li><Link to="/founders-list" className="hover:text-gold transition-colors">The Founder&rsquo;s List</Link></li>
              <li><Link to="/now" className="hover:text-gold transition-colors">Now</Link></li>
              <li><Link to="/resources" className="hover:text-gold transition-colors">Resources</Link></li>
              <li><Link to="/work-with-me" className="hover:text-gold transition-colors">Work With Me</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="eyebrow text-ivory/60 mb-6">Get The Founder&rsquo;s Brief</div>
            <p className="text-ivory/70 text-sm leading-relaxed max-w-sm mb-6">
              A five-minute behind-the-scenes email every Friday. Join founders, entrepreneurs, and visionaries building what&rsquo;s next.
            </p>
            {done ? (
              <div className="font-serif-display text-xl text-ivory/90 leading-snug" data-testid="footer-newsletter-success">
                You&rsquo;re on The Founder&rsquo;s List.
                <p className="mt-2 text-ivory/60 text-sm font-sans">Check your inbox for a welcome note.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" noValidate>
                {/* Honeypot */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                  <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
                </div>
                <div className="border-b border-ivory/25 focus-within:border-gold transition-colors">
                  <input
                    data-testid="footer-newsletter-first-name"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    aria-label="First name"
                    className="w-full bg-transparent py-3 text-sm text-ivory placeholder-ivory/40 focus:outline-none"
                  />
                </div>
                <div className="flex items-center border-b border-ivory/25 focus-within:border-gold transition-colors">
                  <input
                    data-testid={FOOTER.newsletterInput}
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    aria-label="Email"
                    className="flex-1 bg-transparent py-3 text-sm text-ivory placeholder-ivory/40 focus:outline-none"
                  />
                  <button
                    type="submit"
                    data-testid={FOOTER.newsletterSubmit}
                    disabled={loading}
                    className="p-2 text-ivory hover:text-gold transition-colors disabled:opacity-40"
                    aria-label={loading ? 'Joining' : 'Join The Founder\u2019s List'}
                  >
                    <ArrowRight size={18} strokeWidth={1.4} />
                  </button>
                </div>
                <p className="text-ivory/45 text-[10px] leading-relaxed pt-1">
                  By joining, you agree to receive emails from Toia Wallace. Unsubscribe anytime.
                </p>
              </form>
            )}

            <div className="mt-10 flex items-center gap-5">
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-ivory/60 hover:text-gold transition-colors" data-testid="footer-social-linkedin">
                <Linkedin size={18} strokeWidth={1.4} />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-ivory/60 hover:text-gold transition-colors" data-testid="footer-social-instagram">
                <Instagram size={18} strokeWidth={1.4} />
              </a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-ivory/60 hover:text-gold transition-colors" data-testid="footer-social-facebook">
                <Facebook size={18} strokeWidth={1.4} />
              </a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="text-ivory/60 hover:text-gold transition-colors" data-testid="footer-social-tiktok">
                <TikTokIcon size={18} strokeWidth={1.4} />
              </a>
              <a href={SOCIAL_LINKS.linktree} target="_blank" rel="noreferrer" aria-label="Linktree" className="text-ivory/60 hover:text-gold transition-colors" data-testid="footer-social-linktree">
                <LinkIcon size={18} strokeWidth={1.4} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-ivory/10 flex flex-col gap-6">
          <nav aria-label="Legal" className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/terms" data-testid="footer-legal-terms" className="text-ivory/60 hover:text-gold text-[11px] font-label tracking-widest uppercase transition-colors">Terms &amp; Conditions</Link>
            <Link to="/no-refund-policy" data-testid="footer-legal-no-refund" className="text-ivory/60 hover:text-gold text-[11px] font-label tracking-widest uppercase transition-colors">No Refund Policy</Link>
            <Link to="/rescheduling-cancellation-policy" data-testid="footer-legal-rescheduling" className="text-ivory/60 hover:text-gold text-[11px] font-label tracking-widest uppercase transition-colors">Rescheduling &amp; Cancellation Policy</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-ivory/45 text-xs font-label tracking-widest uppercase">© {new Date().getFullYear()} Toia Wallace</div>
              <div className="text-gold/70 text-[10px] font-label tracking-widest uppercase" data-testid="footer-origenne-identifier">ORIGENNE · The Personal Founder HQ of Toia Wallace</div>
            </div>
            <div className="flex items-center gap-6">
              <Link
                to="/founder-command/login"
                data-testid="footer-command-center-link"
                rel="nofollow"
                className="text-ivory/45 hover:text-gold text-xs font-label tracking-widest uppercase transition-colors"
              >
                Command Center
              </Link>
              <div className="text-ivory/45 text-xs font-label tracking-widest uppercase">Founder · Technologist · Speaker</div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
